import React, { useState } from 'react';
import {
  theme,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import LibraryLayout from 'components/Layouts/LibraryLayout';
import { RootState } from 'store/configureStore';
import {
  sampleData,
  areaTitles,
  categoryOptions,
  statusOptions
} from 'constants/dataEntryConstants';
import * as Styled from './DataEntryTable.styled';

const { useToken } = theme;
const { Option } = Select;

const DataEntryTable: React.FC = () => {
  const { token } = useToken();
  const { area } = useParams<{ area: string }>();

  const { scheme } = useSelector((state: RootState) => ({
    scheme: state.global.scheme
  }));
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [form] = Form.useForm();

  // Sample data - will be replaced with API calls
  const [data, setData] = useState(sampleData);

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record: any) => {
    setEditingRecord(record);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const handleDelete = (key: string) => {
    setData(data.filter((item) => item.key !== key));
    message.success('Record deleted successfully');
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status'
    },
    {
      title: 'Created Date',
      dataIndex: 'createdDate',
      key: 'createdDate'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: any) => (
        <Space>
          <Styled.EditButton
            icon={<EditOutlined />}
            size='small'
            onClick={() => handleEdit(record)}
            scheme={scheme}
          >
            Edit
          </Styled.EditButton>
          <Popconfirm
            title='Delete this entry?'
            description='Are you sure you want to delete this entry? This action cannot be undone.'
            onConfirm={() => handleDelete(record.key)}
            okText='Yes, Delete'
            cancelText='Cancel'
            okType='danger'
          >
            <Button
              icon={<DeleteOutlined />}
              size='small'
              danger
              type='primary'
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      if (editingRecord) {
        // Update existing record
        setData(
          data.map((item) =>
            item.key === editingRecord.key ? { ...item, ...values } : item
          )
        );
        message.success('Record updated successfully');
      } else {
        // Add new record
        const newRecord = {
          key: Date.now().toString(),
          id: Date.now().toString(),
          ...values,
          createdDate: new Date().toISOString().split('T')[0]
        };
        setData([...data, newRecord]);
        message.success('Record added successfully');
      }
      setIsModalVisible(false);
      form.resetFields();
    });
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const getAreaTitle = (areaParam: string) => {
    return areaTitles[areaParam] || 'Data Entry';
  };

  return (
    <LibraryLayout>
      <Styled.ContentWrapper token={token} scheme={scheme}>
        <Styled.Title token={token} scheme={scheme}>
          Library - {getAreaTitle(area || '')}
        </Styled.Title>

        <Styled.ButtonSpace>
          <Button type='primary' icon={<PlusOutlined />} onClick={handleAdd}>
            Add New Entry
          </Button>
        </Styled.ButtonSpace>

        <Table
          columns={columns}
          dataSource={data}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 800 }}
        />

        <Modal
          title={editingRecord ? 'Edit Entry' : 'Add New Entry'}
          open={isModalVisible}
          onOk={handleModalOk}
          onCancel={handleModalCancel}
          width={600}
        >
          <Form form={form} layout='vertical' name='entryForm'>
            <Form.Item
              name='name'
              label='Name'
              rules={[{ required: true, message: 'Please input the name!' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name='category'
              label='Category'
              rules={[{ required: true, message: 'Please select a category!' }]}
            >
              <Select placeholder='Select a category'>
                {categoryOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name='status'
              label='Status'
              rules={[{ required: true, message: 'Please select a status!' }]}
            >
              <Select placeholder='Select status'>
                {statusOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </Styled.ContentWrapper>
    </LibraryLayout>
  );
};

export default DataEntryTable;
